// UploadDocuments — /upload-docs/:token
//
// The no-login page a client lands on from a "we just need a couple of
// documents" email. It shows ONLY the documents staff asked for — one upload
// control each — and writes NOTHING to the client's profile. A client who
// already did a full intake months ago must never be asked to re-enter data,
// so there are no form fields here at all.
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, Loader2, ShieldCheck, Upload } from "lucide-react";
import { documentRequests, type DocRequestPublic, type DocRequestItem } from "../lib/api";

type ItemState = { status: "idle" | "uploading" | "done" | "error"; name?: string; error?: string };

export default function UploadDocumentsPage() {
  const { token = "" } = useParams();
  const [state, setState] = useState<DocRequestPublic | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [items, setItems] = useState<Record<string, ItemState>>({});
  const inputs = useRef<Record<string, HTMLInputElement | null>>({});

  useEffect(() => {
    let live = true;
    documentRequests.get(token)
      .then((d) => { if (live) setState(d); })
      .catch((e: any) => { if (live) setLoadErr(e?.message || "This upload link is not valid."); });
    return () => { live = false; };
  }, [token]);

  async function onPick(item: DocRequestItem, file: File | undefined) {
    if (!file) return;
    setItems((s) => ({ ...s, [item.key]: { status: "uploading", name: file.name } }));
    try {
      const r = await documentRequests.upload(token, item.key, file);
      setItems((s) => ({ ...s, [item.key]: { status: "done", name: r.stored_as } }));
    } catch (e: any) {
      setItems((s) => ({ ...s, [item.key]: { status: "error", error: e?.message || "Upload failed — try again." } }));
    }
  }

  const wrap = (children: React.ReactNode) => (
    <div className="landing" style={{ minHeight: "100vh", display: "grid", placeItems: "start center", padding: "40px 18px 70px" }}>
      <div className="landing-card" style={{ width: "min(560px, 100%)", padding: 30 }}>{children}</div>
    </div>
  );

  if (loadErr && !state) return wrap(<><h1 style={{ fontSize: 22, marginBottom: 8 }}>Link problem</h1><p className="muted">{loadErr}</p></>);
  if (!state) return wrap(<p className="muted">Loading…</p>);
  if (state.expired) return wrap(<><h1 style={{ fontSize: 22, marginBottom: 8 }}>This link has expired</h1><p className="muted">Ask {state.company_name} to send a fresh upload link.</p></>);

  const reqItems = state.items || [];
  const allDone = reqItems.length > 0 && reqItems.every((it) => items[it.key]?.status === "done");

  return wrap(
    <>
      <p className="landing-kicker" style={{ marginBottom: 6 }}>{state.company_name}</p>
      <h1 style={{ fontSize: 24, marginBottom: 8 }}>One quick upload, {state.first_name}</h1>
      <p className="muted small" style={{ marginBottom: 6 }}>
        Everything else on your file is set — we just need {reqItems.length === 1 ? "this" : "these"}:
      </p>
      {state.note && <p className="muted small" style={{ marginBottom: 6 }}>{state.note}</p>}
      <p className="muted small" style={{ marginBottom: 22 }}>
        <ShieldCheck size={13} aria-hidden="true" style={{ verticalAlign: -2, marginRight: 5 }} />
        Private to your file. We never sell or share it.
      </p>

      <div className="stacked-form" style={{ gap: 14 }}>
        {reqItems.map((it) => {
          const st = items[it.key]?.status || "idle";
          return (
            <div key={it.key} className="sub-panel" style={{ display: "flex", alignItems: "center", gap: 12, padding: 14 }}>
              <div style={{ flex: 1 }}>
                <strong style={{ fontSize: 15 }}>{it.label}</strong>
                {st === "done" && <div className="muted small" style={{ color: "var(--ne-success, #2c8a3e)" }}>Uploaded ✓ {items[it.key]?.name}</div>}
                {st === "error" && <div className="error-text" style={{ fontSize: 13 }}>{items[it.key]?.error}</div>}
                {st === "uploading" && <div className="muted small">Uploading {items[it.key]?.name}…</div>}
                {st === "idle" && <div className="muted small">Photo or PDF, up to 10 MB</div>}
              </div>
              <input
                ref={(el) => { inputs.current[it.key] = el; }}
                type="file"
                accept="image/*,application/pdf"
                capture="environment"
                style={{ display: "none" }}
                onChange={(e) => onPick(it, e.target.files?.[0])}
              />
              <button
                type="button"
                className={st === "done" ? "ghost-button" : "primary-button"}
                onClick={() => inputs.current[it.key]?.click()}
                disabled={st === "uploading"}
                style={{ whiteSpace: "nowrap" }}
              >
                {st === "uploading" ? <Loader2 size={14} className="spin" /> : st === "done" ? <Upload size={14} /> : <Upload size={14} />}
                {st === "done" ? "Replace" : "Choose file"}
              </button>
            </div>
          );
        })}
      </div>

      {allDone && (
        <div style={{ marginTop: 20, textAlign: "center" }}>
          <CheckCircle2 size={30} aria-hidden="true" style={{ color: "var(--ne-success, #2c8a3e)", marginBottom: 6 }} />
          <p className="muted">That's everything — thank you. {state.company_name} will take it from here. You can close this page.</p>
        </div>
      )}
    </>
  );
}
